import { Icon } from '@iconify/react';

const TYPES_WITHOUT_INVOICE_TYPE = ['RECEIPT', 'ADVANCE', 'JOURNAL_VOUCHER'];

const DocumentSeriesModal = ({
    show,
    handleClose,
    handleSubmit,
    formData,
    setFormData,
    editId,
    documentTypes,
    separatorOptions,
    financialYearFormats,
    months,
    invoiceTypesData,
    generatePreviewFormat,
    loading
}) => {
    if (!show) return null;

    const needsInvoiceType = !TYPES_WITHOUT_INVOICE_TYPE.includes(formData.document_type);

    return (
        <div className="modal-backdrop-custom" onClick={handleClose}>
            <div className="modal-content-custom card radius-16 border-0 shadow-lg" onClick={(e) => e.stopPropagation()}>
                <div className="card-header py-10 px-16 border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="fw-semibold mb-0">{editId ? 'Edit' : 'Add New'} Document Series</h6>
                    <button
                        onClick={handleClose}
                        className="btn btn-link p-0 text-secondary-light"
                    >
                        <Icon icon="mdi:close" className="text-xl" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="card-body p-24" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <div className="row gy-3">
                            {/* Series Name */}
                            <div className="col-12">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Series Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8 h-48-px"
                                    value={formData.series_name}
                                    onChange={(e) => setFormData({ ...formData, series_name: e.target.value })}
                                    placeholder="e.g., Main Invoice Series"
                                    required
                                />
                            </div>

                            {/* Document Type */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Document Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select radius-8 h-48-px"
                                    value={formData.document_type}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        setFormData({
                                            ...formData,
                                            document_type: newType,
                                            ...(TYPES_WITHOUT_INVOICE_TYPE.includes(newType) ? { invoice_type_id: '' } : {})
                                        });
                                    }}
                                    required
                                >
                                    {documentTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Billing Type - only for INVOICE, CREDIT_NOTE, DEBIT_NOTE */}
                            {needsInvoiceType && (
                                <div className="col-6">
                                    <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                        Billing Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select radius-8 h-48-px"
                                        value={formData.invoice_type_id}
                                        onChange={(e) => setFormData({ ...formData, invoice_type_id: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Billing Type</option>
                                        {invoiceTypesData?.map(type => (
                                            <option key={type.invoice_type_id} value={type.invoice_type_id}>{type.invoice_title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {!needsInvoiceType && (
                                <div className="col-6">
                                    <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                        Billing Type
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control radius-8 h-48-px bg-neutral-50"
                                        value="Not Applicable"
                                        disabled
                                    />
                                    <small className="text-secondary-light text-xs mt-1">
                                        {formData.document_type === 'RECEIPT' ? 'Receipt' : formData.document_type === 'ADVANCE' ? 'Advance' : 'Journal Voucher'} series does not require a billing type. Only one series per society is allowed.
                                    </small>
                                </div>
                            )}

                            {/* Prefix */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Prefix <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8 h-48-px"
                                    value={formData.prefix}
                                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                                    placeholder="e.g., INV"
                                    required
                                />
                            </div>

                            {/* Suffix */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Suffix (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8 h-48-px"
                                    value={formData.suffix}
                                    onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
                                    placeholder="e.g., 2024"
                                />
                            </div>

                            {/* Separator */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Separator
                                </label>
                                <select
                                    className="form-select radius-8 h-48-px"
                                    value={formData.separator}
                                    onChange={(e) => setFormData({ ...formData, separator: e.target.value })}
                                >
                                    {separatorOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Start Number */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Start Number <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control radius-8 h-48-px"
                                    value={formData.start_number}
                                    onChange={(e) => setFormData({ ...formData, start_number: parseInt(e.target.value) || 1 })}
                                    placeholder="e.g., 1"
                                    required
                                    min="1"
                                />
                            </div>

                            {/* Number Padding */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Number Padding
                                </label>
                                <input
                                    type="number"
                                    className="form-control radius-8 h-48-px"
                                    value={formData.number_padding}
                                    onChange={(e) => setFormData({ ...formData, number_padding: parseInt(e.target.value) || 4 })}
                                    placeholder="e.g., 4"
                                    min="1"
                                    max="10"
                                />
                            </div>

                            {/* Include Financial Year */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Include Financial Year
                                </label>
                                <select
                                    className="form-select radius-8 h-48-px"
                                    value={formData.include_financial_year}
                                    onChange={(e) => setFormData({ ...formData, include_financial_year: parseInt(e.target.value) })}
                                >
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                </select>
                            </div>

                            {/* Financial Year Format - Only show if include_financial_year is 1 */}
                            {formData.include_financial_year === 1 && (
                                <>
                                    <div className="col-6">
                                        <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                            Financial Year Format
                                        </label>
                                        <select
                                            className="form-select radius-8 h-48-px"
                                            value={formData.financial_year_format}
                                            onChange={(e) => setFormData({ ...formData, financial_year_format: e.target.value })}
                                        >
                                            {financialYearFormats.map(fmt => (
                                                <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-6">
                                        <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                            FY Start Month
                                        </label>
                                        <select
                                            className="form-select radius-8 h-48-px"
                                            value={formData.financial_year_start_month}
                                            onChange={(e) => setFormData({ ...formData, financial_year_start_month: parseInt(e.target.value) })}
                                        >
                                            {months.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-6">
                                        <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                            Reset on FY Change
                                        </label>
                                        <select
                                            className="form-select radius-8 h-48-px"
                                            value={formData.reset_on_financial_year}
                                            onChange={(e) => setFormData({ ...formData, reset_on_financial_year: parseInt(e.target.value) })}
                                        >
                                            <option value={1}>Yes</option>
                                            <option value={0}>No</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Is Default */}
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Set as Default
                                </label>
                                <select
                                    className="form-select radius-8 h-48-px"
                                    value={formData.is_default}
                                    onChange={(e) => setFormData({ ...formData, is_default: parseInt(e.target.value) })}
                                >
                                    <option value={0}>No</option>
                                    <option value={1}>Yes</option>
                                </select>
                            </div>
                        </div>

                        {/* Preview */}
                        {formData.prefix && formData.start_number && (
                            <div className="mt-16 p-16 bg-info-50 radius-8">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <Icon icon="mdi:eye" className="text-info-600" />
                                    <span className="text-sm fw-medium text-info-600">Preview</span>
                                </div>
                                <code className="d-block text-center py-8 bg-white radius-4 fw-bold">
                                    {generatePreviewFormat(formData)}
                                </code>
                            </div>
                        )}
                    </div>
                    <div className="card-footer py-10 px-16 border-top d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn btn-outline-secondary px-20 py-10 radius-8"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn px-20 py-10 radius-8 text-white"
                            style={{ backgroundColor: '#f59e0b' }}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            ) : (
                                <Icon icon={editId ? "mdi:check" : "mdi:plus"} className="text-lg me-1" />
                            )}
                            {editId ? 'Update' : 'Add'} Document Series
                        </button>
                    </div>
                </form>
            </div>

            <style>{`
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    animation: fadeIn 0.2s ease;
                }

                .modal-content-custom {
                    width: 100%;
                    max-width: 600px;
                    margin: 16px;
                    max-height: 90vh;
                    overflow-y: auto;
                    animation: slideUp 0.3s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Compact input height for DocumentSeries modal (was 48px). */
                .h-48-px { height: 34px; font-size: 12px; padding-top: 4px; padding-bottom: 4px; }
            `}</style>
        </div>
    );
};

export default DocumentSeriesModal;
